/**
 * @fileoverview Componente modal de gerenciamento de promoções.
 * Modal adaptativo responsivo integrado ao menu Produtos -> Promoções.
 * Permite listagem, criação, edição e exclusão de promoções com acesso restrito a admin.
 * Interface responsiva com grid adaptável para desktop e mobile.
 * 
 * @module Promotion
 * @requires react
 * @requires @mui/material
 * @requires ../../services/promotionsApi
 * @since 1.0.0
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getAllPromotions, deletePromotion } from '../../services/promotionsApi';
import PromotionForm from './PromotionForm';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';

/**
 * Componente modal principal de gerenciamento de promoções.
 * Modal adaptativo responsivo acessível via menu Produtos -> Promoções (acesso admin).
 * Integrado ao Redux store via promotionSlice para controle de estado.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal (controlado pelo Redux)
 * @param {Function} props.onClose - Callback para fechar modal (dispatch closePromotionManager)
 * @returns {JSX.Element} Modal responsivo com CRUD completo de promoções
 */
export default function Promotion({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { searchAllPromotions } = useGlobalSearch();

  const [promotions, setPromotions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  /**
   * Carrega lista de promoções do backend com filtros.
   * @async
   * @function loadPromotions
   * @returns {Promise<void>}
   */
  const loadPromotions = async () => {
    try {
      setLoading(true);
      const data = await getAllPromotions();
      
      // Aplicar filtro de status
      let filteredPromotions = Array.isArray(data) ? data : [];
      if (statusFilter && statusFilter !== 'todos') {
        filteredPromotions = filteredPromotions.filter(promo => promo.status === statusFilter);
      }
      
      setPromotions(filteredPromotions);
    } catch (error) {
      showSnackbar('Erro ao carregar promoções', 'error');
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPromotions();
    }
  }, [open, statusFilter]);

  /**
   * Manipula ordenação de colunas.
   * @function handleSort
   * @param {string} property - Nome da propriedade para ordenar
   * @returns {void}
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Manipula busca de promoções.
   * @function handleSearch
   * @returns {void}
   */
  const handleSearch = useCallback(() => {
    setSearchTerm(searchInput);
  }, [searchInput]);

  /**
   * Limpa campo de busca e resultados.
   * @function handleClearSearch
   * @returns {void}
   */
  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
  }, []);

  /**
   * Promoções filtradas e ordenadas baseado na busca, coluna e direção selecionadas.
   * Usa pesquisa global que considera TODAS as promoções disponíveis.
   * @constant {Array} sortedPromotions
   */
  const sortedPromotions = useMemo(() => {
    // Usa pesquisa global para garantir busca em TODAS as promoções
    const filtered = searchTerm ? searchAllPromotions(searchTerm) : promotions;
    
    return [...filtered].sort((a, b) => {
      let aValue = '';
      let bValue = '';
      
      if (orderBy === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (orderBy === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (orderBy === 'initial_date') {
        aValue = a.period?.initial_date || '';
        bValue = b.period?.initial_date || '';
      } else if (orderBy === 'final_date') {
        aValue = a.period?.final_date || '';
        bValue = b.period?.final_date || '';
      } else if (orderBy === 'channels') {
        aValue = (a.conditions?.channels?.join(', ') || '').toLowerCase();
        bValue = (b.conditions?.channels?.join(', ') || '').toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [promotions, order, orderBy, searchTerm, searchAllPromotions]);

  /**
   * Exibe mensagem de feedback via Snackbar.
   * @function showSnackbar
   * @param {string} message - Mensagem a ser exibida
   * @param {string} [severity='success'] - Tipo da mensagem
   * @returns {void}
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Abre formulário para criar nova promoção.
   * @function handleCreate
   * @returns {void}
   */
  const handleCreate = () => {
    setFormMode('create');
    setSelectedPromotion(null);
    setFormOpen(true);
  };

  /**
   * Abre formulário para editar promoção.
   * @function handleEdit
   * @param {Object} promotion - Promoção a ser editada
   * @returns {void}
   */
  const handleEdit = (promotion) => {
    setFormMode('edit');
    setSelectedPromotion(promotion);
    setFormOpen(true);
  };

  /**
   * Abre modal de visualização de detalhes da promoção.
   * @function handleView
   * @param {Object} promotion - Promoção a ser visualizada
   * @returns {void}
   */
  const handleView = (promotion) => {
    setSelectedPromotion(promotion);
    setViewOpen(true);
  };

  /**
   * Remove promoção após confirmação.
   * @async
   * @function handleDelete
   * @param {Object} promotion - Promoção a ser excluída
   * @returns {Promise<void>}
   */
  const handleDelete = async (promotion) => {
    if (!window.confirm(`Tem certeza que deseja excluir a promoção "${promotion.name}"?`)) {
      return;
    }

    try {
      await deletePromotion(promotion._id || promotion.id);
      showSnackbar('Promoção excluída com sucesso');
      loadPromotions();
    } catch (error) {
      showSnackbar('Erro ao excluir promoção', 'error');
    }
  };

  /**
   * Callback para quando formulário é salvo com sucesso.
   * @function handleFormSuccess
   * @returns {void}
   */
  const handleFormSuccess = () => {
    setFormOpen(false);
    loadPromotions();
    showSnackbar(
      formMode === 'create' ? 'Promoção criada com sucesso' : 'Promoção atualizada com sucesso'
    );
  };

  /**
   * Retorna cor do chip baseado no status.
   * @function getStatusColor
   * @param {string} status - Status da promoção
   * @returns {string} Cor do chip
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'scheduled': return 'warning';
      case 'draft': return 'default';
      case 'canceled': return 'error';
      default: return 'default';
    }
  };

  /**
   * Renderiza grid desktop com todas as colunas.
   * Colunas ordenáveis com TableSortLabel.
   * @function renderDesktopGrid
   * @returns {JSX.Element}
   */
  const renderDesktopGrid = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleSort('name')}
              >
                Nome
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'status'}
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={() => handleSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'initial_date'}
                direction={orderBy === 'initial_date' ? order : 'asc'}
                onClick={() => handleSort('initial_date')}
              >
                Data Inicial
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'final_date'}
                direction={orderBy === 'final_date' ? order : 'asc'}
                onClick={() => handleSort('final_date')}
              >
                Data Final
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'channels'}
                direction={orderBy === 'channels' ? order : 'asc'}
                onClick={() => handleSort('channels')}
              >
                Canais
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPromotions
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((promotion) => (
              <TableRow key={promotion._id || promotion.id}>
                <TableCell>{promotion.name}</TableCell>
                <TableCell>
                  <Chip 
                    label={promotion.status} 
                    color={getStatusColor(promotion.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{promotion.period?.initial_date || '-'}</TableCell>
                <TableCell>{promotion.period?.final_date || '-'}</TableCell>
                <TableCell>
                  {promotion.conditions?.channels?.join(', ') || '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Visualizar">
                    <IconButton size="small" onClick={() => handleView(promotion)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(promotion)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => handleDelete(promotion)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * Renderiza grid mobile compacto.
   * Usa sortedPromotions para aplicar filtros de busca e ordenação.
   * @function renderMobileGrid
   * @returns {JSX.Element}
   */
  const renderMobileGrid = () => (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Promoção</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPromotions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((promotion) => (
                <TableRow key={promotion._id || promotion.id}>
                  <TableCell>
                    <Box>
                      <Box>{promotion.name}</Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={promotion.status} 
                          color={getStatusColor(promotion.status)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleView(promotion)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(promotion)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(promotion)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciamento de Promoções
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar promoção..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch} title="Buscar">
                    <SearchIcon />
                  </IconButton>
                ),
                endAdornment: searchInput && (
                  <IconButton size="small" onClick={handleClearSearch} title="Limpar">
                    <CloseIcon />
                  </IconButton>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="scheduled">Agendada</MenuItem>
                <MenuItem value="active">Ativa</MenuItem>
                <MenuItem value="expired">Expirada</MenuItem>
                <MenuItem value="canceled">Cancelada</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Adicionar
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>Carregando...</Box>
          ) : (
            isMobile ? renderMobileGrid() : renderDesktopGrid()
          )}

          <TablePagination
            component="div"
            count={sortedPromotions.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Formulário */}
      <PromotionForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        mode={formMode}
        promotion={selectedPromotion}
        onSuccess={handleFormSuccess}
        onError={(message) => showSnackbar(message, 'error')}
      />

      {/* Modal de Visualização */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes da Promoção</DialogTitle>
        <DialogContent>
          {selectedPromotion && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box><strong>Nome:</strong> {selectedPromotion.name}</Box>
              <Box><strong>Status:</strong> {selectedPromotion.status}</Box>
              <Box><strong>Período:</strong> {selectedPromotion.period?.initial_date} até {selectedPromotion.period?.final_date}</Box>
              <Box><strong>Tipo de Desconto:</strong> {selectedPromotion.discount?.type}</Box>
              <Box><strong>Valor:</strong> {selectedPromotion.discount?.value} {selectedPromotion.discount?.currency}</Box>
              <Box><strong>Descrição:</strong> {selectedPromotion.context?.description || '-'}</Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}